import React, { useCallback, useEffect, useState } from 'react';
import { ModalWrapper } from '../modals/ModalWrapper';
import {
  Communication, CommChannel, CommDirection, communicationService,
} from '../../services/communicationService';
import CommunicationsPanel from './CommunicationsPanel';
import CommunicationDetailModal from './CommunicationDetailModal';
import ComposeEmailDialog from './ComposeEmailDialog';
import { useUserTimezone } from '../../hooks/useUserTimezone';
import { getApiError } from '../../utils/apiError';

/**
 * A lead's email and SMS history, with the compose box.
 *
 * Patients get this as a panel on their detail page; leads have no detail page
 * (their actions live on the list), so the same panel is shown in a modal. The
 * panel, the message viewer and the compose box are reused unchanged - a lead's
 * conversation is the same object as a patient's and reads the same way, which
 * matters because one becomes the other.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  leadId: number;
  leadName: string;
  leadEmail: string | null;
}

const LeadCommunicationsDialog: React.FC<Props> = ({
  open, onClose, leadId, leadName, leadEmail,
}) => {
  const userTimezone = useUserTimezone();
  const [items, setItems] = useState<Communication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<'' | CommChannel>('');
  const [directionFilter, setDirectionFilter] = useState<'' | CommDirection>('');
  const [selected, setSelected] = useState<Communication | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await communicationService.getLeadCommunications(leadId, {
        channel: channelFilter || undefined,
        direction: directionFilter || undefined,
        page_size: 100,
      });
      setItems(res.items);
    } catch (err) {
      setError(getApiError(err, 'Failed to load communications'));
    } finally {
      setIsLoading(false);
    }
  }, [leadId, channelFilter, directionFilter]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <>
      <ModalWrapper
        isOpen={open}
        onClose={onClose}
        title={`Communications: ${leadName}`}
        width="max-w-3xl"
        titleAlign="left"
        titleClassName="text-lg font-bold text-[#366A75] leading-tight"
      >
        {error && (
          <div className="mb-3 px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        <CommunicationsPanel
          items={items}
          isLoading={isLoading}
          channelFilter={channelFilter}
          directionFilter={directionFilter}
          onChannelFilter={setChannelFilter}
          onDirectionFilter={setDirectionFilter}
          onOpenMessage={setSelected}
          onCompose={() => setComposeOpen(true)}
          // Nothing to send to without an address; the panel greys the button.
          canCompose={!!leadEmail}
          userTimezone={userTimezone}
        />
      </ModalWrapper>

      <ComposeEmailDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        entity="lead"
        patientId={leadId}
        patientName={leadName}
        patientEmail={leadEmail}
        onSent={() => { setComposeOpen(false); load(); }}
      />

      <CommunicationDetailModal
        comm={selected}
        onClose={() => setSelected(null)}
        userTimezone={userTimezone}
      />
    </>
  );
};

export default LeadCommunicationsDialog;
